import {useState, useRef, useEffect, FC, ComponentType} from "react"
import {SxProps, Typography} from "@mui/material"
import {Tooltip} from "../../atoms/Tooltip"

type Props = {
  text: string
  lines: number
  fontSize: number
  color?: string
  withEllipsis?: boolean
  lineHeight?: string
  tooltipComponent?: ComponentType<any>
  fullText?: string
  alwaysShowTooltip?: boolean
  useWidth?: boolean
  styles?: React.CSSProperties
  sx?: SxProps
  parentRef?: any
}

const EllipsisTypography: FC<Props> = ({
  text,
  lines = 2,
  fontSize = 14,
  color,
  withEllipsis = true,
  lineHeight = "none",
  tooltipComponent = Tooltip,
  fullText,
  alwaysShowTooltip,
  styles,
  sx,
  parentRef,
}) => {
  const [isOverflowed, setIsOverflowed] = useState(false)
  const textRef = useRef(null)
  const [screenSize, setScreenSize] = useState(window.innerHeight)
  const tooltipText = fullText ? fullText : text

  useEffect(() => {
    if (parentRef) {
      const scrollableElements = parentRef.current as unknown as HTMLElement
      scrollableElements?.addEventListener("scroll", () => {
        const tooltip = document.querySelector('[role="tooltip"]')
        if (tooltip) {
          ;(tooltip as HTMLElement).style.display = "none"
        }
      })
    }
  }, [parentRef])

  const checkOverflow = () => {
    if (textRef.current) {
      const {scrollHeight, clientHeight} = textRef.current
      setIsOverflowed(scrollHeight > clientHeight)
    }
  }
  const showTooltip = alwaysShowTooltip ? false : !isOverflowed || !withEllipsis
  useEffect(() => {
    checkOverflow()
  }, [screenSize])

  const TooltipComponent = tooltipComponent

  return (
    <TooltipComponent
      title={isOverflowed || alwaysShowTooltip ? tooltipText : ""}
      disableHoverListener={showTooltip}
      onMouseMove={() => setScreenSize(window.innerWidth)}
      onMouseEnter={checkOverflow}
      leaveDelay={0}
    >
      <Typography
        ref={textRef}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: lines,
          fontSize: fontSize + "px",
          WebkitBoxOrient: "vertical",
          lineBreak: lines === 1 ? "anywhere" : "normal",
          lineHeight: lineHeight,
          color: color,
          ...styles,
        }}
        sx={sx}
      >
        {text}
      </Typography>
    </TooltipComponent>
  )
}

export default EllipsisTypography
